import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { ConvertedControlsHubPanelObservationActions } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';
import {
    convertYesNoToBoolean,
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import { cm2Panel_OpraQueries } from '@queries/generated/central-monitoring.generated';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

type Props = {
    studyRef: string;
    observationId: string;
    dataset: CM2Dataset;
    panelData?: cm2Panel_OpraQueries;
};

export const CmControlsActionsGrid = ({ studyRef, observationId, dataset, panelData }: Props) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.popOut.obsActions.actionsGrid' });
    const { t: tShared, ready } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { data } = ControlsHubApi.useGetControlsHubChartObservationActions(studyRef, observationId);
    const { data: datasets } = DatasetApi.useGetActive(studyRef);
    const [gridData, setGridData] = React.useState<unknown[]>([]);
    const [datasetList, setDatasetList] = React.useState<string[] | undefined>(undefined);
    const { ActiveInactiveCell, TooltipCell } = useGridCustomCell();
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    // eslint-disable-next-line max-len
    const exportFileName = `${studyName}_Dataset ${stringDateWithoutTimeFromUtc(dataset.dataCutDate, 'DD-MMM-YYYY')}_${t('exportFileName')}_${panelData?.cm2Panel?.plotTitle}`;

    useEffect(() => {
        if (datasets) {
            const sortedDatasets = datasets
                .filter((d) => d.dataCutDate != null) // exclude undefined/null
                .sort((a, b) => new Date(b.dataCutDate!).getTime() - new Date(a.dataCutDate!).getTime())
                .map((d) => stringDateWithoutTimeFromUtc(d.dataCutDate!, 'DD-MMM-yyyy')!);

            setDatasetList(sortedDatasets);
        }
    }, [datasets]);

    useEffect(() => {
        if (data && ready && datasetList) {
            const convertedResult: ConvertedControlsHubPanelObservationActions[] = data.map((item) => {
                return {
                    ...item,
                    owner: {
                        name: item.owner,
                        active: convertYesNoToBoolean(item.owner_active),
                    },
                    association: {
                        name: item.association,
                        active: convertYesNoToBoolean(item.association_active),
                    },
                    panel: {
                        id: item.panel_id,
                        name: item.panel,
                        active: convertYesNoToBoolean(item.panel_active),
                    },
                    responsible_function: {
                        name: item.responsible_function,
                        active: convertYesNoToBoolean(item.responsible_function_active),
                    },
                    time_period: stringDateWithoutTimeFromUtc(item.time_period),
                    requested_date: stringDateWithoutTimeFromUtc(item.requested_date),
                    due_date: stringDateWithoutTimeFromUtc(item.due_date),
                    completed_date: stringDateWithoutTimeFromUtc(item.completed_date),
                    completed_date_populated_on: stringDateWithoutTimeFromUtc(item.completed_date_populated_on),
                    updated_date: stringDateWithoutTimeFromUtc(item.updated_date),
                    created_date: stringDateWithoutTimeFromUtc(item.created_date),
                };
            });
            setGridData(convertedResult);
        }
    }, [data, ready, datasetList]);

    if (!datasetList) return;

    const columns: GridColumnProps[] = [
        {
            id: 'status',
            field: 'status',
            title: t('columns.status'),
            filter: 'text',
            minWidth: 150,
            lovData: [tShared('shared.open'), tShared('shared.completed')],
        },
        {
            id: 'associatedReviewLog',
            field: 'associated_review_log',
            title: t('columns.associatedReviewLog'),
            lovData: datasetList,
            minWidth: 200,
        },
        {
            id: 'description',
            field: 'description',
            title: t('columns.description'),
            lockedByDefault: true,
            filter: 'text',
            minWidth: 250,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'comments',
            field: 'comments',
            title: t('columns.comments'),
            filter: 'text',
            minWidth: 200,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'resolution',
            field: 'resolution',
            title: t('columns.resolution'),
            filter: 'text',
            minWidth: 200,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'responsible_function',
            field: 'responsible_function',
            title: t('columns.responsibleFunction'),
            filter: 'text',
            minWidth: 180,
            filterKey: 'name',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'owner',
            field: 'owner',
            title: t('columns.owner'),
            filter: 'text',
            minWidth: 150,
            filterKey: 'name',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'requestedDate',
            field: 'requested_date',
            title: t('columns.requestedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'dueDate',
            field: 'due_date',
            title: t('columns.dueDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'completedDate',
            field: 'completed_date',
            title: t('columns.completedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'completedDatePopulatedOn',
            field: 'completed_date_populated_on',
            title: t('columns.completedDatePopulatedOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 200,
        },
        {
            id: 'updatedDate',
            field: 'updated_date',
            title: t('columns.updatedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'updatedBy',
            field: 'updated_by',
            title: t('columns.updatedBy'),
            filter: 'text',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'createdDate',
            field: 'created_date',
            title: t('columns.createdDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'createdBy',
            field: 'created_by',
            title: t('columns.createdBy'),
            filter: 'text',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'id',
            field: 'record_id',
            title: t('columns.id'),
            filter: 'text',
            minWidth: 100,
            showByDefault: false,
        },
    ];

    return (
        <Grid
            excludeHiddenRowMenuActions
            title={t('title')}
            maxHeight="100%"
            testDataId="ram-cm-controls-actions-grid"
            data={gridData}
            columns={columns}
            filterable
            showAddIcon={false}
            useLoadingOverlay
            scrollable="virtual"
            dataItemKey="id"
            exportFilename={`${exportFileName}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
            service={AuditServiceType.CORE}
            area={AuditApplicationArea.RISK_ASSESSMENT}
            dataType={AuditDataType.EXCEL_EXPORT}
            persistentStorageKey={String(PersistentStorageKeys['cm-controls-popout-controls-actions'])}
        />
    );
};
